/**
 * map.ts — pure helpers for the South Tyrol hospital map.
 *
 * Marker-position helpers, focus-order logic, and GeoJSON winding defence.
 * No React, no side effects — fully unit-testable.
 *
 * The projection itself is built and owned by the <Mercator> component
 * (via fitExtent); these helpers take a resolved GeoProjection and derive
 * pixel coords from it.
 *
 * § focus order: calmest-first, matching the card list's rankHospitals
 *   order (owner's explicit call: keyboard order = visual card order).
 *
 * § winding rewind: d3-geo uses the SPHERICAL right-hand rule to determine
 *   whether a ring is an exterior (small area, < 2π sr) or a hole over the
 *   entire globe (≈ 4π sr).  A ring that passes the 2-D planar shoelace test
 *   as "CCW" may still read as a whole-globe hole under d3's spherical
 *   convention, causing `fitExtent` to fall back to its default scale (~46)
 *   and projecting every marker to the same pixel.
 *
 *   `ensureCCW` therefore tests winding via d3-geo's own `geoArea`:
 *     • geoArea ≈ 4π  → ring is a globe-covering hole → reverse it
 *     • geoArea ≪ 2π  → ring is already a small exterior → leave it alone
 *   This is self-correcting regardless of how the on-disk asset is wound
 *   and cannot be broken by a future re-vendor or manual "helpful" rewind.
 */

import { geoArea } from "@visx/vendor/d3-geo";
import type { Feature, Polygon, GeoJsonProperties } from "geojson";
import type { HospitalQueueState } from "@/api/generated/model";
import { rankHospitals } from "./congestion";

// ── Re-export tightly-typed GeoJSON aliases ────────────────────────────────

/** The single South Tyrol polygon feature loaded from /geo/south-tyrol.geojson. */
export type ProvinceFeature = Feature<Polygon, GeoJsonProperties>;

// ── GeoProjection interface (minimal — enough to project a point) ──────────

/** Minimal interface matching d3-geo GeoProjection's call signature. */
export interface PointProjection {
  (coords: [number, number]): [number, number] | null;
}

// ── Winding rewind (defense-in-depth against mis-wound GeoJSON assets) ───────

/**
 * Returns the feature with its exterior ring wound correctly for d3-geo.
 *
 * d3-geo uses the SPHERICAL right-hand rule: a small exterior ring has
 * `geoArea` ≪ 2π steradians; a ring that covers the globe (a "hole") has
 * `geoArea` ≈ 4π.  When the exterior ring is globe-covering, `fitExtent`
 * falls back to its default scale (~46), projecting every point to the same
 * pixel.
 *
 * The threshold 2π (half the sphere) is used: any legitimate province
 * polygon will be far below this; a globe-covering ring will be near 4π.
 * If the ring is already small-area the original feature is returned
 * unchanged (no allocation).  If it is globe-covering it is reversed and a
 * new feature/geometry is returned so callers can detect the correction.
 *
 * Scope: single-ring simple Polygon only.  A future re-vendor that ships
 * a holed Polygon (rings.length > 1) or a MultiPolygon would need the
 * per-ring treatment; this function throws rather than silently half-fixing
 * such an asset so the problem surfaces immediately in tests and local dev.
 */
export function ensureCCW(feature: ProvinceFeature): ProvinceFeature {
  const rings = feature.geometry.coordinates;
  const exterior = rings[0];
  if (!exterior || exterior.length < 4) return feature;

  // Defensive guard: holes and MultiPolygon require per-ring handling that
  // this function does not implement.  Throw loudly so a re-vendor that
  // changes the asset geometry fails in tests/local dev, not silently in prod.
  if (rings.length > 1) {
    throw new Error(
      `ensureCCW: expected a single-ring Polygon but got ${rings.length} rings. ` +
        "Update ensureCCW to handle holes before using this asset.",
    );
  }

  // Use d3-geo's spherical area test.  A globe-covering ring (≈ 4π sr)
  // means d3 reads it as a hole, not an exterior — reverse it.
  if (geoArea(feature) <= Math.PI * 2) return feature;

  // Reverse the exterior ring (no holes to preserve — guarded above).
  const reversedExterior = [...exterior].reverse();
  return {
    ...feature,
    geometry: {
      ...feature.geometry,
      coordinates: [reversedExterior],
    },
  };
}

// ── Marker positioning ─────────────────────────────────────────────────────

/**
 * Project (latitude, longitude) → (x, y) pixel coordinates using a
 * resolved d3-geo projection (received from the <Mercator> children callback).
 * Returns `null` if the projection returns null (coordinate outside range).
 */
export function projectCoord(
  projection: PointProjection,
  latitude: number,
  longitude: number,
): [number, number] | null {
  const pt = projection([longitude, latitude]);
  if (!pt) return null;
  return [pt[0], pt[1]];
}

// ── Focus order ─────────────────────────────────────────────────────────────

/**
 * Returns hospitals in focus-order: calmest-first (rank 1 = shortest queue),
 * no-data hospitals last. Matches the card list's `rankHospitals` order.
 */
export function focusOrderedHospitals(hospitals: HospitalQueueState[]): HospitalQueueState[] {
  return rankHospitals(hospitals, "asc").map((r) => r.hospital);
}

// ── Congestion token → marker variant ─────────────────────────────────────

export type MarkerVariant = "calm" | "moderate" | "busy" | "unknown";

/**
 * Marker CSS variant from a congestion level.
 * "calm" → slate (neutral), "moderate" → violet, "busy" → pulse (rose).
 * Never uses triage colours (quarantined) per DESIGN.md.
 */
export function markerVariant(level: string): MarkerVariant {
  if (level === "calm") return "calm";
  if (level === "moderate") return "moderate";
  if (level === "busy") return "busy";
  return "unknown";
}
